import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { colorForSite } from "../../../utils/featureDistribution";

// Grouped bar chart for a categorical feature.
// One x-axis tick per `feature.categories[]` entry; within each tick, one
// thin bar per site. Same SVG-without-library approach as NumericHistogram.
//
// Inputs (categorical feature from /datasets/feature-distribution):
//   feature.categories: string[K]   (shared by all sites; may include "Other")
//   feature.distributions: [
//     { site_id, display_name, counts: int[K] or "<5", n_valid, n_missing }
//   ]
//
// __label__ feature: same shape; we render it identically. The card wrapper
// adds the "LABEL" pill.
const CategoricalBars = ({ feature, height = 140 }) => {
  const data = useMemo(() => {
    const cats = Array.isArray(feature?.categories) ? feature.categories : [];
    const dists = Array.isArray(feature?.distributions) ? feature.distributions : [];
    let maxCount = 0;
    for (const d of dists) {
      const counts = Array.isArray(d.counts) ? d.counts : [];
      for (const c of counts) {
        if (typeof c === 'number' && Number.isFinite(c) && c > maxCount) maxCount = c;
      }
    }
    return { cats, dists, maxCount };
  }, [feature]);

  const { cats, dists, maxCount } = data;
  if (cats.length === 0 || dists.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fx-text-muted)', fontSize: '0.75rem' }}>
        No categories to plot
      </Box>
    );
  }

  const PAD_L = 26;
  const PAD_R = 6;
  const PAD_T = 6;
  // Categorical labels can be long ("Bronchus_and_lung"); reserve more
  // bottom padding and render them rotated 30deg.
  const PAD_B = 36;
  const VIEW_W = 280;
  const VIEW_H = height;
  const plotW = VIEW_W - PAD_L - PAD_R;
  const plotH = VIEW_H - PAD_T - PAD_B;

  const slotW = plotW / cats.length;
  const groupGap = Math.max(0.5, slotW * 0.18);
  const barAreaW = slotW - groupGap;
  const barW = Math.max(0.5, barAreaW / Math.max(1, dists.length));

  const yMax = Math.max(1, maxCount);
  const yTicks = 4;
  const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) =>
    Math.round((yMax * i) / yTicks)
  );

  // Truncate category labels longer than ~10 chars; show full on tooltip.
  const truncate = (s, n = 10) =>
    typeof s === 'string' && s.length > n ? `${s.slice(0, n - 1)}…` : (s ?? '');

  return (
    <Box sx={{ width: '100%', height }}>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        {/* Y-axis gridlines + ticks */}
        {yTickVals.map((v, i) => {
          const y = PAD_T + plotH - (plotH * (v / yMax));
          return (
            <g key={`y-${i}`}>
              <line
                x1={PAD_L}
                x2={PAD_L + plotW}
                y1={y}
                y2={y}
                stroke="var(--fx-border)"
                strokeWidth={0.6}
                strokeDasharray="2,2"
              />
              <text
                x={PAD_L - 3}
                y={y + 3}
                textAnchor="end"
                fontSize="7"
                fill="var(--fx-text-muted)"
              >
                {v}
              </text>
            </g>
          );
        })}

        {/* Bars: per category × per site */}
        {cats.map((cat, catIdx) => {
          const slotX = PAD_L + catIdx * slotW + groupGap / 2;
          return (
            <g key={`cat-${catIdx}`}>
              {dists.map((d, siteIdx) => {
                const raw = d.counts?.[catIdx];
                const isSuppressed = typeof raw === 'string';
                const value = typeof raw === 'number' ? raw : 0;
                const h = isSuppressed
                  ? Math.max(2, plotH * 0.08)
                  : (plotH * value) / yMax;
                const x = slotX + siteIdx * barW;
                const y = PAD_T + plotH - h;
                const color = isSuppressed ? 'var(--fx-border-strong)' : colorForSite(d.site_id, siteIdx);
                const title = isSuppressed
                  ? `${d.display_name || d.site_id} · ${cat}: suppressed (<5)`
                  : `${d.display_name || d.site_id} · ${cat}: ${value}`;
                return (
                  <Tooltip key={`bar-${catIdx}-${siteIdx}`} title={title} placement="top">
                    <rect
                      x={x}
                      y={y}
                      width={Math.max(0.5, barW - 0.3)}
                      height={Math.max(0, h)}
                      fill={color}
                      opacity={isSuppressed ? 0.65 : 0.92}
                      rx={0.5}
                    />
                  </Tooltip>
                );
              })}
            </g>
          );
        })}

        {/* X-axis labels (rotated to fit) */}
        {cats.map((cat, catIdx) => {
          const cx = PAD_L + catIdx * slotW + slotW / 2;
          const cy = PAD_T + plotH + 8;
          return (
            <Tooltip key={`xl-${catIdx}`} title={cat}>
              <text
                x={cx}
                y={cy}
                textAnchor="end"
                fontSize="7"
                fill="var(--fx-text-secondary)"
                transform={`rotate(-30 ${cx} ${cy})`}
              >
                {truncate(cat)}
              </text>
            </Tooltip>
          );
        })}

        {/* X-axis baseline */}
        <line
          x1={PAD_L}
          x2={PAD_L + plotW}
          y1={PAD_T + plotH}
          y2={PAD_T + plotH}
          stroke="var(--fx-border-strong)"
          strokeWidth={0.6}
        />
      </svg>
    </Box>
  );
};

export default CategoricalBars;
